import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { ref, onValue, update, remove } from "firebase/database";
import { useAuth } from "../context/AuthContext";
import { Bell, ArrowLeft, Trash2, CheckCheck } from "lucide-react";

const OFFER_TYPES = new Set(["offer","offer_accepted","offer_declined","offer_countered"]);
const TRANSACTION_TYPES = new Set(["buy_intent","buy_cancelled","buy_expired","sale_confirmed","interest","not_interested","rate_seller","pending_purchase","whatsapp_request","whatsapp_approved","whatsapp_declined"]);
const ALERT_TYPES = new Set(["price_drop","alert_match","expiry_reminder","seller_nudge","cross_college_request","request_approved","request_rejected"]);

const TYPE_ICON = {
  offer_countered: "↩️",
  buy_intent: "🛍️",
  buy_cancelled: "❌",
  buy_expired: "⏰",
  sale_confirmed: "✅",
  interest: "👀",
  not_interested: "💨",
  offer: "💰",
  offer_accepted: "✅",
  offer_declined: "❌",
  price_drop: "📉",
  cross_college_request: "📩",
  request_approved: "✅",
  request_rejected: "❌",
  rate_seller: "⭐",
  alert_match: "🔔",
  expiry_reminder: "⏰",
  whatsapp_request: "📱",
  whatsapp_approved: "✅",
  whatsapp_declined: "❌",
  support_resolved: "✅",
  admin_broadcast: "📢",
};

function timeAgo(ts) {
  if (!ts) return "";
  const secs = Math.floor((Date.now() - ts) / 1000);
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  if (secs < 7 * 86400) return `${Math.floor(secs / 86400)}d ago`;
  return new Date(ts).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

export default function Notifications() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");

  useEffect(() => {
    if (!currentUser) return;
    const notifRef = ref(db, `notifications/${currentUser.uid}`);
    const unsub = onValue(notifRef, (snap) => {
      if (snap.exists()) {
        const items = Object.entries(snap.val())
          .map(([id, data]) => ({ id, ...data }))
          .sort((a, b) => b.createdAt - a.createdAt);
        setNotifications(items);
      } else {
        setNotifications([]);
      }
      setLoading(false);
    });
    return () => unsub();
  }, [currentUser]);

  const unreadCount = notifications.filter(n => !n.read).length;

  async function markAllRead() {
    if (!currentUser || unreadCount === 0) return;
    const updates = {};
    notifications.filter(n => !n.read).forEach(n => {
      updates[`notifications/${currentUser.uid}/${n.id}/read`] = true;
    });
    await update(ref(db), updates);
  }

  async function deleteNotif(id, e) {
    e.preventDefault();
    e.stopPropagation();
    await remove(ref(db, `notifications/${currentUser.uid}/${id}`));
  }

  async function markRead(id) {
    await update(ref(db, `notifications/${currentUser.uid}/${id}`), { read: true });
  }

  // Mark all read when page opens
  useEffect(() => {
    if (!loading && unreadCount > 0) markAllRead();
  }, [loading]); // eslint-disable-line

  if (!currentUser) return null;

  return (
    <div className="pb-24 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white border-b px-4 py-4 sticky top-14 z-10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1.5 -ml-1 rounded-full hover:bg-gray-100">
            <ArrowLeft size={20} className="text-gray-700" />
          </button>
          <div>
            <h2 className="text-lg font-bold text-gray-800">Notifications</h2>
            {unreadCount > 0 && <p className="text-xs text-blue-600">{unreadCount} unread</p>}
          </div>
        </div>
        {notifications.length > 0 && (
          <button
            onClick={markAllRead}
            className="flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-800 px-3 py-1.5 rounded-xl bg-blue-50"
          >
            <CheckCheck size={14} /> Mark all read
          </button>
        )}
      </div>

      {/* Filter Tabs */}
      <div className="px-4 pt-3 pb-2 flex gap-2 overflow-x-auto no-scrollbar">
        {[
          { key: "all", label: "All" },
          { key: "offers", label: "💰 Offers" },
          { key: "transactions", label: "🛍️ Buys/Sells" },
          { key: "alerts", label: "🔔 Alerts" },
          { key: "system", label: "📢 System" },
        ].map(f => (
          <button key={f.key} onClick={() => setActiveFilter(f.key)}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition border ${activeFilter === f.key ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-200"}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="px-4 pt-1 space-y-2 max-w-lg mx-auto">
        {loading ? (
          [1, 2, 3, 4].map(i => (
            <div key={i} className="h-16 bg-white rounded-xl animate-pulse" />
          ))
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <Bell size={52} className="text-gray-200 mb-4" />
            <p className="text-gray-600 font-semibold text-lg mb-1">No notifications yet</p>
            <p className="text-gray-400 text-sm">You'll see buy requests, price drops, and more here.</p>
          </div>
        ) : (
          notifications.filter(n => {
            if (activeFilter === "all") return true;
            if (activeFilter === "offers") return OFFER_TYPES.has(n.type);
            if (activeFilter === "transactions") return TRANSACTION_TYPES.has(n.type);
            if (activeFilter === "alerts") return ALERT_TYPES.has(n.type);
            if (activeFilter === "system") return n.type === "admin_broadcast" || n.type === "support_resolved";
            return true;
          }).map(n => (
            <Link
              key={n.id}
              to={n.listingId ? `/listing/${n.listingId}` : "#"}
              onClick={() => markRead(n.id)}
              className={`flex items-start gap-3 p-4 rounded-xl border transition ${
                !n.read ? "bg-blue-50 border-blue-100" : "bg-white border-gray-100 hover:border-gray-200"
              }`}
            >
              <span className="text-xl flex-shrink-0 mt-0.5">{TYPE_ICON[n.type] || "🔔"}</span>
              <div className="flex-1 min-w-0">
                {n.title && <p className="text-sm font-semibold text-gray-900 leading-snug">{n.title}</p>}
                <p className={`text-sm leading-snug ${!n.read ? "font-semibold text-gray-900" : "text-gray-700"}`}>
                  {n.body || n.message}
                </p>
                <p className="text-xs text-gray-400 mt-1">{timeAgo(n.createdAt)}</p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {!n.read && <span className="w-2 h-2 bg-blue-500 rounded-full" />}
                <button
                  onClick={e => deleteNotif(n.id, e)}
                  className="p-1 rounded-full text-gray-300 hover:text-red-400 hover:bg-red-50 transition"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}
